import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Zap, Save, Activity, Trash2, DollarSign, Scale } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function AISettings() {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [daily, setDaily] = useState(null);
  const [retentionBusy, setRetentionBusy] = useState(false);
  const [retentionLog, setRetentionLog] = useState([]);
  const [calibrating, setCalibrating] = useState(false);
  const [calibHistory, setCalibHistory] = useState([]);
  const [actualInput, setActualInput] = useState("");
  const [calibDays, setCalibDays] = useState(7);

  useEffect(() => {
    api.get("/ai/admin/config").then((r) => {
      setConfig(r.data);
      setSelected(r.data.default_model);
    });
    api.get("/ai/admin/metrics?days=7").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/ai/admin/daily-cost?days=14").then((r) => setDaily(r.data)).catch(() => {});
    api.get("/admin/retention/log?limit=10").then((r) => setRetentionLog(r.data.items || [])).catch(() => {});
    api.get("/ai/admin/calibrations?limit=5").then((r) => setCalibHistory(r.data.items || [])).catch(() => {});
  }, []);

  async function submitCalibration() {
    const actual = parseFloat(actualInput);
    if (!actual || actual <= 0) { toast.error("Enter a positive USD amount"); return; }
    setCalibrating(true);
    try {
      const { data } = await api.post("/ai/admin/calibrate", { actual_cost_usd: actual, days: calibDays });
      const drift = data.per_model_drift || {};
      const driftLabel = Object.entries(drift).map(([k, v]) => `${k}×${v}`).join(" · ");
      toast.success(`Calibrated · drift ${driftLabel}`);
      setActualInput("");
      // Refresh both cost surfaces + history
      const [m, d, h] = await Promise.all([
        api.get("/ai/admin/metrics?days=7"),
        api.get("/ai/admin/daily-cost?days=14"),
        api.get("/ai/admin/calibrations?limit=5"),
      ]);
      setMetrics(m.data); setDaily(d.data); setCalibHistory(h.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Calibration failed");
    } finally {
      setCalibrating(false);
    }
  }

  async function runRetention() {
    setRetentionBusy(true);
    try {
      const { data } = await api.post("/admin/retention/run");
      const s = data.summary || {};
      const total = (s.ai_call_metrics?.deleted || 0) + (s.click_events?.deleted || 0) + (s.email_log?.deleted || 0);
      toast.success(`Deleted ${total} rows across 3 collections`);
      const log = await api.get("/admin/retention/log?limit=10");
      setRetentionLog(log.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retention sweep failed (super_admin only)");
    } finally {
      setRetentionBusy(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      await api.post("/ai/admin/config", { default_model: selected });
      toast.success(`Platform default → ${selected.toUpperCase()}`);
      setConfig((c) => ({ ...c, default_model: selected }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed (super_admin only)");
    } finally {
      setSaving(false);
    }
  }

  if (!config) return <div className="text-slate-500 text-sm">Loading…</div>;

  const dirty = selected !== config.default_model;
  const buckets = metrics?.by_model_endpoint || [];

  return (
    <div data-testid="admin-ai-settings">
      <div className="pf-chip pf-chip-cyan"><Sparkles className="w-3 h-3" /> AI</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">
        AI model
      </h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        A/B toggle for the LLM that powers shopper recommendations, similar-coupon
        widgets, and merchant copy nudges. Individual requests can still override
        via <code>?model=…</code> — this only sets the fallback. Shoppers can also
        lock in their own preference from their dashboard.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 max-w-3xl">
        {config.available_models.map((m) => {
          const isSelected = selected === m.key;
          const isCurrent = config.default_model === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setSelected(m.key)}
              className={`pf-card p-5 text-left transition ${
                isSelected ? "ring-2 ring-cyan-400 shadow-md" : "hover:ring-1 hover:ring-slate-200"
              }`}
              data-testid={`ai-model-${m.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="pf-chip pf-chip-lav uppercase text-[10px]">
                  {m.provider}
                </div>
                {isCurrent && (
                  <span className="pf-chip pf-chip-mint text-[10px]">Active</span>
                )}
              </div>
              <div className="mt-3 font-display text-xl text-slate-900 uppercase tracking-tight">
                {m.key === "gpt" ? <>GPT <Zap className="inline w-4 h-4 text-cyan-500" /></> : "Claude"}
              </div>
              <div className="text-xs text-slate-500 mt-1 font-mono">{m.model_id}</div>
              <div className="text-xs text-slate-500 mt-3 leading-relaxed">
                {m.key === "claude"
                  ? "Anthropic · strong at nuanced copy + long-form reasoning."
                  : "OpenAI · very fast, great at structured JSON extraction."}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="ai-model-save"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : dirty ? "Save default" : "Saved"}
        </button>
        <span className="text-xs text-slate-500">
          {dirty
            ? `You're about to switch the platform default to ${selected.toUpperCase()}.`
            : `Current platform default: ${config.default_model.toUpperCase()}.`}
        </span>
      </div>

      {/* --- A/B analytics --- */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="pf-chip pf-chip-lav"><Activity className="w-3 h-3" /> Analytics</div>
            <h3 className="font-display text-2xl mt-2 text-slate-900">A/B performance (last 7 days)</h3>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">{metrics?.total_calls ?? 0} total AI calls</div>
            <div className="font-mono text-sm text-slate-800 mt-1" data-testid="metrics-total-cost">
              <DollarSign className="inline w-3 h-3" /> {(metrics?.total_cost_usd ?? 0).toFixed(4)} USD est.
            </div>
          </div>
        </div>

        {buckets.length === 0 ? (
          <div className="pf-card p-6 text-sm text-slate-500 text-center">
            No AI calls recorded yet. Fire a few <code>/api/ai/recommendations</code> requests to populate this table.
          </div>
        ) : (
          <div className="pf-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Model</th>
                  <th className="px-5 py-3">Endpoint</th>
                  <th className="px-5 py-3 text-right">Calls</th>
                  <th className="px-5 py-3 text-right">Success</th>
                  <th className="px-5 py-3 text-right">Avg latency</th>
                  <th className="px-5 py-3 text-right">P95 latency</th>
                  <th className="px-5 py-3 text-right">Output chars</th>
                  <th className="px-5 py-3 text-right">Avg $ / call</th>
                  <th className="px-5 py-3 text-right" title="Cost divided by successful calls only — the 'real value delivered' price">Avg $ / success</th>
                  <th className="px-5 py-3 text-right">Total $</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b, i) => (
                  <tr key={i} className="border-t border-white/60" data-testid={`metric-${b.model_key}-${b.endpoint}`}>
                    <td className="px-5 py-3">
                      <span className={`pf-chip ${b.model_key === "gpt" ? "pf-chip-cyan" : "pf-chip-lav"}`}>
                        {b.model_key.toUpperCase()}
                      </span>
                      <span className="ml-2 text-[11px] text-slate-500 font-mono">{b.model_id}</span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-600">{b.endpoint}</td>
                    <td className="px-5 py-3 text-right font-mono">{b.count}</td>
                    <td className="px-5 py-3 text-right font-mono">
                      <span className={b.success_rate >= 0.95 ? "text-emerald-600" : b.success_rate >= 0.8 ? "text-amber-600" : "text-rose-600"}>
                        {(b.success_rate * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono">{b.avg_latency_ms.toLocaleString()}ms</td>
                    <td className="px-5 py-3 text-right font-mono">{b.p95_latency_ms.toLocaleString()}ms</td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-500">{b.total_output_chars.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-600">US${b.avg_cost_usd.toFixed(6)}</td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-600">
                      {b.avg_cost_per_success_usd != null ? `US$${b.avg_cost_per_success_usd.toFixed(6)}` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-slate-900 font-semibold">US${b.total_cost_usd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {metrics?.pricing_note && (
          <p className="text-[11px] text-slate-400 mt-2">{metrics.pricing_note}</p>
        )}
      </div>

      {/* --- Daily cost breakdown (invoice calibration) --- */}
      <div className="mt-12" data-testid="daily-cost-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="pf-chip pf-chip-lav"><DollarSign className="w-3 h-3" /> Calibration</div>
            <h3 className="font-display text-2xl mt-2 text-slate-900">Daily cost breakdown</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Compare these estimates against your Emergent daily statements. If our estimate is consistently off by factor <b>K</b>, multiply <code>AI_PRICE_CLAUDE_USD_PER_1M_OUT</code> / <code>AI_PRICE_GPT_USD_PER_1M_OUT</code> by <b>K</b> to recalibrate. Current prices:
              {daily?.prices && Object.entries(daily.prices).map(([k, v]) => (
                <span key={k} className="ml-2 font-mono">{k}=US${v}/1M</span>
              ))}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Last {daily?.days ?? 14} days</div>
            <div className="font-mono text-sm text-slate-800 mt-1">
              <DollarSign className="inline w-3 h-3" /> {(daily?.grand_total_cost_usd ?? 0).toFixed(4)} USD est.
            </div>
          </div>
        </div>

        {(daily?.series?.length ?? 0) === 0 ? (
          <div className="pf-card p-6 text-sm text-slate-500 text-center">
            No AI calls in the last 14 days.
          </div>
        ) : (
          <>
            {/* Recharts line chart — Claude vs GPT daily cost */}
            <div className="pf-card p-4" data-testid="daily-cost-chart">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={daily.series.map((d) => ({
                    day: d.day.slice(5),
                    claude: d.by_model.find((m) => m.model_key === "claude")?.cost_usd ?? 0,
                    gpt: d.by_model.find((m) => m.model_key === "gpt")?.cost_usd ?? 0,
                    total: d.total_cost_usd,
                  }))}
                  margin={{ top: 12, right: 20, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickFormatter={(v) => `US$${v.toFixed(4)}`}
                    width={70}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v) => `US$${Number(v).toFixed(6)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="claude" name="Claude" stroke="#a5b4fc" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="gpt" name="GPT" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#0f172a" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Raw table below the chart */}
            <div className="pf-card overflow-hidden mt-4">
              <table className="w-full text-sm">
                <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Day (UTC)</th>
                    <th className="px-5 py-3 text-right">Claude calls</th>
                    <th className="px-5 py-3 text-right">Claude $ est.</th>
                    <th className="px-5 py-3 text-right">GPT calls</th>
                    <th className="px-5 py-3 text-right">GPT $ est.</th>
                    <th className="px-5 py-3 text-right">Day total $</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.series.slice().reverse().map((d) => {
                    const claude = d.by_model.find((m) => m.model_key === "claude");
                    const gpt = d.by_model.find((m) => m.model_key === "gpt");
                    return (
                      <tr key={d.day} className="border-t border-white/60" data-testid={`daily-${d.day}`}>
                        <td className="px-5 py-3 font-mono text-xs text-slate-600">{d.day}</td>
                        <td className="px-5 py-3 text-right font-mono">{claude?.calls ?? 0}</td>
                        <td className="px-5 py-3 text-right font-mono text-xs text-slate-600">US${(claude?.cost_usd ?? 0).toFixed(6)}</td>
                        <td className="px-5 py-3 text-right font-mono">{gpt?.calls ?? 0}</td>
                        <td className="px-5 py-3 text-right font-mono text-xs text-slate-600">US${(gpt?.cost_usd ?? 0).toFixed(6)}</td>
                        <td className="px-5 py-3 text-right font-mono text-slate-900 font-semibold">US${d.total_cost_usd.toFixed(6)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* --- Auto-calibrator --- */}
      <div className="mt-12" data-testid="calibrator-panel">
        <div className="pf-chip pf-chip-cyan"><Scale className="w-3 h-3" /> Calibrate</div>
        <h3 className="font-display text-2xl mt-2 text-slate-900">Auto-calibrate against a real invoice</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          Paste the exact USD amount from your Emergent daily statement for the same window shown above. We'll compute drift <b>K = actual / estimated</b> and update the per-model prices so future estimates match. Persisted to <code>platform_config.id=ai_pricing</code>; history in <code>pricing_calibrations</code>.
        </p>
        <div className="flex items-end gap-3 mt-4 flex-wrap">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Window (days)</label>
            <input
              type="number" min="1" max="90"
              value={calibDays}
              onChange={(e) => setCalibDays(parseInt(e.target.value) || 7)}
              className="w-24 px-3 py-2 rounded-lg border border-slate-200 bg-white/70 text-sm font-mono"
              data-testid="calib-days"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Actual USD from invoice</label>
            <input
              type="number" step="0.0001" min="0"
              value={actualInput}
              onChange={(e) => setActualInput(e.target.value)}
              placeholder="e.g. 0.2432"
              className="w-44 px-3 py-2 rounded-lg border border-slate-200 bg-white/70 text-sm font-mono"
              data-testid="calib-actual"
            />
          </div>
          <button
            onClick={submitCalibration}
            disabled={calibrating}
            className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="calib-submit"
          >
            <Scale className="w-3 h-3" /> {calibrating ? "Calibrating…" : "Calibrate now"}
          </button>
        </div>

        {calibHistory.length > 0 && (
          <div className="pf-card overflow-hidden mt-6">
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">When</th>
                  <th className="px-5 py-3 text-right">Window</th>
                  <th className="px-5 py-3 text-right">Estimated</th>
                  <th className="px-5 py-3 text-right">Actual</th>
                  <th className="px-5 py-3 text-right">Drift</th>
                  <th className="px-5 py-3 text-right">New prices (US$/1M)</th>
                </tr>
              </thead>
              <tbody>
                {calibHistory.map((c) => (
                  <tr key={c.id} className="border-t border-white/60">
                    <td className="px-5 py-3 font-mono text-xs text-slate-600">{new Date(c.created_at).toISOString().slice(0, 16).replace("T", " ")}</td>
                    <td className="px-5 py-3 text-right font-mono">{c.days}d</td>
                    <td className="px-5 py-3 text-right font-mono text-xs">US${c.estimated_cost_usd.toFixed(6)}</td>
                    <td className="px-5 py-3 text-right font-mono text-xs">US${c.actual_cost_usd.toFixed(6)}</td>
                    <td className="px-5 py-3 text-right font-mono text-xs">
                      {Object.entries(c.per_model_drift || {}).map(([k, v]) => (
                        <span key={k} className={`ml-1 ${v > 1 ? "text-rose-600" : v < 1 ? "text-emerald-600" : ""}`}>{k}×{v}</span>
                      ))}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-xs">
                      {Object.entries(c.new_prices || {}).map(([k, v]) => (
                        <span key={k} className="ml-2">{k}=${v}</span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Retention cutoffs (P3 hardening) --- */}
      <div className="mt-12" data-testid="retention-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="pf-chip pf-chip-lav"><Trash2 className="w-3 h-3" /> Retention</div>
            <h3 className="font-display text-2xl mt-2 text-slate-900">Telemetry retention cutoffs</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Daily cron (03:15 UTC) purges old rows from <code>ai_call_metrics</code> (90d),
              <code> click_events</code> (400d), and <code>email_log</code> (400d). Override via
              <code> RETENTION_DAYS_*</code> env vars. Trigger a manual sweep below.
            </p>
          </div>
          <button
            onClick={runRetention}
            disabled={retentionBusy}
            className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="retention-run"
          >
            <Trash2 className="w-3 h-3" /> {retentionBusy ? "Running…" : "Run now"}
          </button>
        </div>

        {retentionLog.length === 0 ? (
          <div className="pf-card p-6 text-sm text-slate-500 text-center">
            No retention runs recorded yet. Trigger one above or wait for the daily cron.
          </div>
        ) : (
          <div className="pf-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Run at (UTC)</th>
                  <th className="px-5 py-3 text-right">ai_call_metrics</th>
                  <th className="px-5 py-3 text-right">click_events</th>
                  <th className="px-5 py-3 text-right">email_log</th>
                  <th className="px-5 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {retentionLog.map((r) => {
                  const s = r.summary || {};
                  const total = (s.ai_call_metrics?.deleted || 0) + (s.click_events?.deleted || 0) + (s.email_log?.deleted || 0);
                  return (
                    <tr key={r.id} className="border-t border-white/60">
                      <td className="px-5 py-3 font-mono text-xs text-slate-600">{new Date(r.run_at).toISOString().slice(0, 19).replace("T", " ")}</td>
                      <td className="px-5 py-3 text-right font-mono">{s.ai_call_metrics?.deleted || 0} <span className="text-slate-400 text-[10px]">/ {s.ai_call_metrics?.days || 0}d</span></td>
                      <td className="px-5 py-3 text-right font-mono">{s.click_events?.deleted || 0} <span className="text-slate-400 text-[10px]">/ {s.click_events?.days || 0}d</span></td>
                      <td className="px-5 py-3 text-right font-mono">{s.email_log?.deleted || 0} <span className="text-slate-400 text-[10px]">/ {s.email_log?.days || 0}d</span></td>
                      <td className="px-5 py-3 text-right font-mono font-semibold">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
